import React, { useState, useCallback } from "react";
import '../../css/index.css';
import jp from 'jsonpath';
import { useTranslation } from 'react-i18next';

const PathTesterPagina = () => {
    const [inputText, setInputText] = useState("");
    const [outputText, setOutputText] = useState("");
    const [path, setPath] = useState("");
    const { t } = useTranslation();

    const handleExecutePathBtn = useCallback(() => {
        if (inputText.trim()[0] === '{' || inputText.trim()[0] === '[') {
            handleJsonPath();
        } else {
            handleXPath();
        }
    }, [inputText, path]);

    const handleXPath = useCallback(() => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(inputText, "application/xml");

        const parserError = xmlDoc.querySelector("parsererror");
        if (parserError) {
            setOutputText("Invalid XML: " + parserError.textContent);
            return;
        }

        let result;
        try {
            result = xmlDoc.evaluate(
                path,
                xmlDoc,
                null,
                XPathResult.ANY_TYPE,
                null
            );
        } catch (ex) {
            setOutputText(ex.message);
            return;
        }

        let value;

        switch (result.resultType) {
            case XPathResult.STRING_TYPE:
                value = result.stringValue;
                break;

            case XPathResult.NUMBER_TYPE:
                value = result.numberValue;
                break;

            case XPathResult.BOOLEAN_TYPE:
                value = result.booleanValue;
                break;

            case XPathResult.UNORDERED_NODE_ITERATOR_TYPE:
            case XPathResult.ORDERED_NODE_ITERATOR_TYPE: {
                const serializer = new XMLSerializer();
                let nodes = "";
                let node = result.iterateNext();
                while (node) {
                    nodes += serializer.serializeToString(node) + '\n';
                    node = result.iterateNext();
                }
                value = nodes;
                break;
            }

            default:
                value = "No match";
        }

        if (typeof value === "object") {
            value = value.textContent;
        }

        setOutputText(value);
    }, [inputText, path]);

    const handleJsonPath = useCallback(() => {
        try {
            const jsonData = JSON.parse(inputText);
            const result = jp.query(jsonData, path);
            setOutputText(JSON.stringify(result, null, 2));
        } catch (ex) {
            setOutputText(ex.message);
        }
    }, [inputText, path]);

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();

        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();

            reader.onload = (e) => {
                setInputText(e.target.result);
            };

            reader.readAsText(file);
        }
    };

    return (
        <div className="relative size-full">
            <div className="flex flex-row items-start size-full gap-5">
                <div className="flex flex-col px-0 py-2 size-full">

                    {/* Header */}
                    <div className="bg-[#00aaad] dark:bg-[#008b8e] w-full border border-gray-400 border-solid">
                        <div className="flex justify-center py-[10px]">
                            <p className="text-[20px] font-bold text-center">
                                {t("pathTester.header")}
                            </p>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="bg-[rgba(243,244,246,0.75)] dark:bg-[rgba(55,65,81,0.75)] w-full border border-gray-400 border-solid">

                        {/* Input Row */}
                        <div className="flex items-center py-[20px] px-[6.5%] w-[55%]">
                            <label className="w-[170px]">{t("pathTester.pathLabel")}</label>
                            <input
                                value={path}
                                onChange={(e) => setPath(e.target.value)}
                                className="bg-white dark:bg-gray-700 h-[26px] border border-black w-full"
                            />
                            <button
                                onClick={handleExecutePathBtn}
                                className="bg-[#dedede] dark:bg-[#3F3F46] px-[5px] border border-black"
                            >
                                {t("pathTester.runBtn")}
                            </button>
                        </div>

                        {/* Text Areas */}
                        <div className="flex gap-[3%] py-[10px] w-full justify-center">

                            {/* Input */}
                            <div
                                className="bg-white dark:bg-gray-700 w-[42%]"
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                            >
                                <textarea
                                    className="w-full h-[1000px] p-[5px] bg-transparent text-black dark:text-white resize-none border border-black border-solid"
                                    placeholder={t("pathTester.inputPlaceholder")}
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                />
                            </div>

                            {/* Output */}
                            <div className="bg-white dark:bg-gray-700 w-[42%]">
                                <textarea
                                    className="w-full h-[1000px] p-[5px] bg-transparent text-black dark:text-white resize-none border border-black border-solid"
                                    placeholder={t("pathTester.outputPlaceholder")}
                                    value={outputText}
                                    readOnly
                                />
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PathTesterPagina;